import type { ConflictCode } from '../../services/api/types.gen';

/**
 * Map a change-task-type failure {@link ConflictCode} to short, user-facing
 * copy.
 *
 * Every refusal here is permanent for that visit, so the copy has to say why
 * rather than invite a retry: told only "please try again", a clinician's next
 * move is to delete and recreate the task, which throws away the chart this
 * feature exists to keep. The server `detail` is intentionally never surfaced
 * (see `.cursor/rules/error-handling.mdc`); we key off the stable `code`.
 * Unknown / missing codes degrade to the generic retry phrasing, which is the
 * right advice for a transient failure.
 *
 * Keyed by the generated `ConflictCode` union rather than free-form strings so
 * that renaming a code in `app/error_codes.py` and regenerating the client
 * fails the build here, instead of silently falling through to the generic
 * retry copy this function exists to avoid.
 */
const COPY_BY_CODE: Partial<Record<ConflictCode, string>> = {
  VISIT_TYPE_CHANGE_BILLED:
    'This task is already on a claim, billing cycle, or payroll batch, so its type can no longer be changed.',
  VISIT_TYPE_CHANGE_MISSED:
    'Missed tasks cannot change type. Un-mark it as missed first.',
  VISIT_LOCKED:
    'This task is signed or completed, so its type can no longer be changed.',
  VISIT_TYPE_NOT_INTERCHANGEABLE:
    'That task type is no longer available for this task. Reopen the task and try again.',
};

export function changeVisitTypeErrorCopy(code?: ConflictCode | null): string {
  return (
    (code && COPY_BY_CODE[code]) ??
    'Unable to change task type. Please try again.'
  );
}
